import { create } from 'zustand';
import {
  creativeStrategyApi,
  type CreativeStrategyCitations,
  type CreativeStrategyEntityClass,
  type CreativeStrategyItem,
  type CreativeStrategyPackDetail,
  type CreativeStrategyPackSummary,
} from '@/services/api';

export type PackTab = 'concepts' | 'strategies' | 'templates' | 'gaps' | 'citations' | 'provenance';

export const TAB_CLASS: Partial<Record<PackTab, CreativeStrategyEntityClass>> = {
  concepts: 'market_concept',
  strategies: 'creative_strategy',
  templates: 'creative_pattern',
};

interface Filters {
  search: string;
  showHidden: boolean;
  level: string;
}

interface CreativeStrategyState {
  packs: CreativeStrategyPackSummary[];
  pack: CreativeStrategyPackDetail | null;
  citations: CreativeStrategyCitations | null;
  selectedItemId: string | null;
  tab: PackTab;
  filters: Filters;

  loading: boolean;
  packLoading: boolean;
  saving: boolean;
  error: string | null;

  fetchPacks: () => Promise<void>;
  fetchPack: (clientUid: string, version?: string | null) => Promise<void>;
  fetchCitations: (clientUid: string) => Promise<void>;
  setTab: (tab: PackTab) => void;
  setFilters: (partial: Partial<Filters>) => void;
  selectItem: (id: string | null) => void;
  pin: (clientUid: string, version: string | null) => Promise<void>;
  setItemOverride: (clientUid: string, itemId: string, hidden: boolean, note?: string | null) => Promise<void>;

  selectedItem: () => CreativeStrategyItem | null;
  visibleItems: () => CreativeStrategyItem[];
}

export const useCreativeStrategyStore = create<CreativeStrategyState>((set, get) => ({
  packs: [],
  pack: null,
  citations: null,
  selectedItemId: null,
  tab: 'concepts',
  filters: { search: '', showHidden: true, level: '' },

  loading: false,
  packLoading: false,
  saving: false,
  error: null,

  fetchPacks: async () => {
    set({ loading: true, error: null });
    try {
      const res = await creativeStrategyApi.listPacks();
      set({ packs: res.packs, loading: false });
    } catch (err) {
      set({ error: (err as Error).message, loading: false });
    }
  },

  fetchPack: async (clientUid, version) => {
    set({ packLoading: true, error: null });
    try {
      const pack = await creativeStrategyApi.getPack(clientUid, version);
      set({ pack, packLoading: false });
    } catch (err) {
      set({ error: (err as Error).message, packLoading: false, pack: null });
    }
  },

  fetchCitations: async (clientUid) => {
    try {
      const citations = await creativeStrategyApi.citations(clientUid);
      set({ citations });
    } catch (err) {
      set({ citations: { client_uid: clientUid, available: false, reason: (err as Error).message, items: {}, angles: [] } });
    }
  },

  setTab: (tab) => set({ tab, selectedItemId: null }),
  setFilters: (partial) => set((s) => ({ filters: { ...s.filters, ...partial } })),
  selectItem: (id) => set({ selectedItemId: id }),

  pin: async (clientUid, version) => {
    set({ saving: true, error: null });
    try {
      await creativeStrategyApi.pinVersion(clientUid, version);
      // Re-read the served view: pinning changes which version the platform serves.
      await get().fetchPack(clientUid, null);
      set({ saving: false });
    } catch (err) {
      set({ error: (err as Error).message, saving: false });
    }
  },

  setItemOverride: async (clientUid, itemId, hidden, note) => {
    set({ saving: true, error: null });
    try {
      const res = await creativeStrategyApi.setItemOverride(clientUid, itemId, { hidden, note });
      set((s) => ({
        saving: false,
        pack: s.pack
          ? {
              ...s.pack,
              hidden_count: res.hidden_count,
              items: s.pack.items.map((i) =>
                i.resolved_id === itemId ? { ...i, hidden: res.hidden, note: res.note } : i,
              ),
            }
          : s.pack,
      }));
    } catch (err) {
      set({ error: (err as Error).message, saving: false });
    }
  },

  selectedItem: () => {
    const { pack, selectedItemId } = get();
    if (!pack || !selectedItemId) return null;
    return pack.items.find((i) => i.resolved_id === selectedItemId) ?? null;
  },

  visibleItems: () => {
    const { pack, tab, filters } = get();
    const cls = TAB_CLASS[tab];
    if (!pack || !cls) return [];
    const q = filters.search.trim().toLowerCase();
    return pack.items.filter((i) => {
      if (i.entity_class !== cls) return false;
      if (!filters.showHidden && i.hidden) return false;
      if (filters.level) {
        const levels = (i.data.levels as string[] | undefined) ?? [];
        if (!levels.includes(filters.level)) return false;
      }
      if (q) {
        const hay = `${i.canonical_name} ${i.evidence.join(' ')} ${JSON.stringify(i.data)}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  },
}));
